import * as React from 'react';
import { SketchPicker } from 'react-color';
import { Button, Input, Modal, Radio, Divider } from 'antd';

import { ColorSquare } from '../../../../../../components/Others/ColorSquare/ColorSquare';
import { DynamicInputs, IField } from '../../../../../../components/Others/DynamicInputs/DynamicInputs';
import Form, { FormItem } from '../../../../../../components/Form';

export interface IAttributeValueDetailProps {
  attribute: GQL.IAttribute;
  value: GQL.IAttributeValue;
  languages: Array<GQL.ILanguage>;
  onSave: (value: GQL.IAttributeValue) => void;
  onCancel: () => void;
}

export interface IAttributeValueDetailState {
  value: GQL.IAttributeValue;
  showColorPicker: boolean;
}

export default class AttributeValueDetail extends React.Component<IAttributeValueDetailProps, IAttributeValueDetailState> {

  constructor(props: IAttributeValueDetailProps) {
    super(props);

    this.state = {
      value: props.value,
      showColorPicker: false
    };

    this.save = this.save.bind(this);
  }

  public render() {
    const { attribute } = this.props;

    return (
      <Modal
        maskClosable={false}
        style={{ top: '5vh' }}
        width={'40%'}
        title={`Attribute value${attribute.title ? ` of "${attribute.title}"` : ``}`}
        visible={true}
        footer={null}
        onCancel={() => this.props.onCancel()}
      >
        <div>
          <Modal
            maskClosable={false}
            style={{ top: '5vh' }}
            width={'40%'}
            bodyStyle={{ minHeight: '60vh' }}
            title={`Pick color`}
            visible={this.state.showColorPicker}
            okText={'Ok'}
            onOk={() => this.setState({ showColorPicker: false })}
            onCancel={() => this.setState({ showColorPicker: false })}
          >
            <SketchPicker
              color={this.state.value.colorHex || ''}
              onChangeComplete={color => this.onChange('colorHex', color.hex)}
            />
          </Modal>
          <Form 
            onSubmit={this.save}
            onCancel={() => this.props.onCancel()}
          >
            <FormItem label="Name" required={true}>
              <Input
                name={'title'}
                autoFocus={true}
                required={true}
                placeholder="Title"
                addonBefore={'Internal'}
                value={this.state.value.title}
                onChange={e => this.onChange('title', e.target.value)}
              />
            </FormItem>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center'
              }}
            >
              {this.props.attribute.isVisual && (
                <Radio.Group
                  onChange={val => {
                    if (val.target.value === 'image') {
                      this.onChange('colorHex', undefined);
                    } else {
                      this.onChange('colorHex', '');
                    }
                  }}
                  value={this.state.value.colorHex == undefined ? 'image' : 'color'}
                >
                  <Radio.Button value={'image'}>Image</Radio.Button>
                  <Radio.Button value={'color'}>Color</Radio.Button>
                </Radio.Group>
              )}
              {this.state.value.colorHex != undefined && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    marginLeft: '5px'
                  }}
                >
                  <ColorSquare hex={this.state.value.colorHex} />
                  <Button
                    onClick={() =>
                      this.setState({
                        showColorPicker: true
                      })
                    }
                  >
                    Choose
                  </Button>
                </div>
              )}
            </div>

            <Divider />

            {this.props.languages.map((l, i) => {
              if (this.props.attribute.definition === undefined) {
                return (
                  <React.Fragment key={l.id}>
                    <FormItem label={`Value`} required={true}>
                      <Input
                        name={'value'}
                        required={true}
                        addonBefore={l.value}
                        placeholder={`Value. For example "red" or "XXL"`}
                        value={this.getValue(l.value)}
                        onChange={e => this.onChange('value', e.target.value, l.value)}
                      />
                    </FormItem>
                    <FormItem label={`Slug`} required={true}>
                      <Input
                        name={'slug'}
                        required={true}
                        placeholder={`Slug`}
                        addonBefore={l.value}
                        value={this.getSlug(l.value)}
                        onChange={e => this.onChange('slug', e.target.value, l.value)}
                      />
                    </FormItem>
                    {i != this.props.languages.length - 1 && <Divider />}
                  </React.Fragment>
                );
              }

              const defs = [...this.props.attribute.definition || []];

              return (
                <React.Fragment key={l.id}>
                  <DynamicInputs
                    editable={false}
                    disabled={false}
                    addonBefore={l.value}
                    defaultFields={defs.map((f: IField, i: number) => {
                      return {
                        key: i,
                        label: `${f.value}`,
                        value:
                          this.getValue(l.value) != undefined
                            ? this.getValue(l.value)[f.value]
                            : ''
                      } as IField;
                    })}
                    onChange={values => this.setValue(values, l.value)}
                  />

                  {defs === undefined || defs.length === 0 &&
                    <FormItem label={`Value`} required={true}>
                      <Input
                        name={'value'}
                        required={true}
                        placeholder={`Value`}
                        addonBefore={l.value}
                        value={this.getValue(l.value)}
                        onChange={e => this.onChange('value', e.target.value, l.value)}
                      />
                    </FormItem>}

                  <FormItem label={`Slug`} required={true}>
                    <Input
                      name={'slug'}
                      required={true}
                      placeholder={`Slug`}
                      addonBefore={l.value}
                      value={this.getSlug(l.value)}
                      onChange={e => this.onChange('slug', e.target.value, l.value)}
                    />
                  </FormItem>

                  {i != this.props.languages.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </Form>
        </div>
      </Modal>
    );
  }

  private setValue(value: string | Array<IField>, language: string) {
    let newValue;

    if (typeof value === 'string') {
      newValue = {
        ...this.state.value.value,
        [language]: (value || '')
      };
    } else {
      const obj = {};
      (value as Array<IField>).map(f => {
        if (f.label != undefined) {
          obj[f.label] = (f.value || '').toUpperCase();
        }
      });

      newValue = {
        ...this.state.value.value,
        [language]: obj
      };
    }

    this.setState({
      value: {
        ...this.state.value,
        value: newValue
      }
    });
  }

  private setSlug(slug: string, language: string) {
    let newSlug;

    if (typeof slug === 'string') {
      newSlug = {
        ...this.state.value.slug,
        [language]: slug
      };
    }

    this.setState({
      value: {
        ...this.state.value,
        slug: newSlug
      }
    });
  }

  private getValue(language: string) {
    if (this.state.value.value) {
      return this.state.value.value[language];
    }

    return undefined;
  }

  private getSlug(language: string) {
    if (this.state.value.slug) {
      return this.state.value.slug[language];
    }

    return undefined;
  }

  private async onChange(key: string, val: any, language?: string) {
    if (key === 'value') {
      if (language) {
        this.setValue(val, language);
      }
    } else if (key === 'slug') {
      if (language) {
        this.setSlug(val, language);
      }
    } else {
      this.setState({
        value: { ...this.state.value, [key]: val }
      });
    }
  }

  private save() {
    this.props.onSave(this.state.value);
  }
}



// WEBPACK FOOTER //
// ./src/scenes/Attributes/AttributeDetail/components/Values/components/AttributeValueDetail.tsx